import * as THREE from 'three';
import { eventBus, Events } from '../core/EventBus.js';
import { gameState } from '../core/GameState.js';
import { CAMERA, ROW_DEPTH, JUICE } from '../core/Constants.js';

const DEG = Math.PI / 180;

/**
 * Chases the player's row on z only — x stays centred so the playfield never
 * slides sideways under the player.
 *
 * The rig sits on one fixed line of sight and slides along it until the
 * playfield fits the viewport, so a phone in portrait gets the same framing a
 * desktop does rather than a cropped one. Also owns screen shake.
 */
export class CameraRig {
  constructor(camera) {
    this.camera = camera;
    this.z = 0;
    this.lift = CAMERA.START_LIFT;
    this.shake = 0;
    this.dist = CAMERA.DIST_RANGE[0];
    this.target = new THREE.Vector3();

    // Unit vector from the aim point back to the camera.
    this.dir = new THREE.Vector3();
    this.updateDir();

    this.unsub = eventBus.on(Events.SHAKE, (a) => {
      this.shake = Math.max(this.shake, a ?? JUICE.SHAKE_DEATH);
    });
  }

  /** View direction from the current CAMERA shape — re-run after tuning it. */
  updateDir() {
    this.dir.set(0, CAMERA.HEIGHT, CAMERA.BACK + CAMERA.LOOK_AHEAD).normalize();
  }

  /** Called on resize: pick the FOV and pull back far enough to fit the yard. */
  fit(aspect) {
    const fov = aspect < 1 ? CAMERA.FOV_PORTRAIT : CAMERA.FOV_LANDSCAPE;
    this.camera.fov = fov;
    this.camera.aspect = aspect;

    const halfV = Math.tan((fov / 2) * DEG);
    const needed = CAMERA.VIEW_HALF_WIDTH / (aspect * halfV);
    this.dist = THREE.MathUtils.clamp(needed, ...CAMERA.DIST_RANGE);
    this.camera.updateProjectionMatrix();
    this.updateDir();
    this.apply();
  }

  snap() {
    this.z = -gameState.player.row * ROW_DEPTH;
    this.lift = CAMERA.START_LIFT;
    this.shake = 0;
    this.apply();
  }

  update(dt) {
    const wanted = -gameState.player.row * ROW_DEPTH;
    this.z += (wanted - this.z) * (1 - Math.exp(-CAMERA.FOLLOW_LERP * dt));
    this.lift *= Math.exp(-2.2 * dt);
    if (this.lift < 0.02) this.lift = 0;
    this.shake = Math.max(0, this.shake - (JUICE.SHAKE_DECAY * this.shake + 0.4) * dt);
    this.apply();
  }

  apply() {
    const s = this.shake;
    const jx = s ? (Math.random() - 0.5) * s * 2.2 : 0;
    const jy = s ? (Math.random() - 0.5) * s * 2.2 : 0;

    this.target.set(jx * 0.5, 0, this.z - CAMERA.LOOK_AHEAD);
    this.camera.position
      .copy(this.dir)
      .multiplyScalar(this.dist)
      .add(this.target);
    this.camera.position.y += this.lift + jy;
    this.camera.position.x += jx;
    this.camera.lookAt(this.target);
  }

  dispose() { this.unsub(); }
}
